import { inject, Injectable } from '@angular/core';
import { addDoc, collection, collectionData, CollectionReference, deleteDoc, doc, DocumentData, DocumentReference, DocumentSnapshot, FieldPath, FieldValue, Firestore, getCountFromServer, getDoc, getDocs, limit, orderBy, Query, query, QueryConstraint, QueryDocumentSnapshot, QuerySnapshot, setDoc, SetOptions, startAfter, Timestamp, where, WhereFilterOp } from '@angular/fire/firestore';
import { BehaviorSubject, from, map, Observable, of, Subject, switchMap, tap } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class FstoreService {

  private firestore = inject(Firestore);

  constructor() { }

  fscollection(collectionName:string){
    return new FSCollection(this, collectionName);
  }

  collection(collectionName:string){
    return collection(this.firestore, collectionName);
  }

  read<T extends DocumentData>(collection: CollectionReference, id:string|undefined):Observable<T>{
    if (id) {
      const docRef = doc(collection,id);
      const snap:Promise<T> = getDoc(docRef)
        .then((docref:DocumentSnapshot)=>docref.data())
        .then(docData=>{ if (docData) docData['id']=id; return docData as T;});
      return from(snap);
    } else {
      return of({} as T);
    }
  }


  // Query documentation  https://firebase.google.com/docs/firestore/query-data/queries
  list<T>(collection: CollectionReference){

    // get documents (data) from the collection using collectionData
    return collectionData(collection,{idField:'id'}) as Observable<T[]>;
    
  }

  create<T>(collection:CollectionReference, data:any){
    return from(addDoc(collection, data)) as Observable<DocumentReference<T>>;
    // .pipe(map((documentReference: DocumentReference) => {
    //     // the documentReference provides access to the newly created document
    // }));
  }

  update<T>(collection:CollectionReference, id:string, data:Partial<Updateable<T>>,options:SetOptions = {}){
    const docRef = doc(collection,id);
    return from(setDoc(docRef,data,options));
  }

  delete(collection:CollectionReference, id:string){
    const docRef = doc(collection,id);
    return from(deleteDoc(docRef));
  }


  query<T>(collection:CollectionReference):QueryDesc<T>{
    return new QueryDesc<T>(collection);
  }

  count(collection:CollectionReference){
    return from(getCountFromServer(collection)).pipe(
      map(qSnap=>qSnap.data().count),
    ) as Observable<number>;;
  }
}

class FSCollection {
  _fs:FstoreService;
  collectionId:string;
  collRef:CollectionReference;

  constructor(fs:FstoreService, refId:string){
    this._fs = fs;
    this.collectionId = refId;
    this.collRef = this._fs.collection(this.collectionId);
  }

  read<T extends DocumentData>(id:string|undefined):Observable<T>{
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.read.apply(this._fs, args as Parameters<FstoreService['read']>) as Observable<T>;
  }


  // Query documentation  https://firebase.google.com/docs/firestore/query-data/queries
  list<T>(){
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.list.apply(this._fs, args as Parameters<FstoreService['list']>) as Observable<T>;
  }

  create<T>(data:any){
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.create.apply(this._fs, args as Parameters<FstoreService['create']>) as Observable<DocumentReference<T>>;
  }

  update<T>(id:string, data:Partial<Updateable<T>>,options:SetOptions = {}){
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.update.apply(this._fs, args as Parameters<FstoreService['update']>) as Observable<T>;
  }

  delete(id:string){
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.delete.apply(this._fs, args as Parameters<FstoreService['delete']>);
  }


  query<T>():QueryDesc<T>{
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.query.apply(this._fs, args as Parameters<FstoreService['query']>) as QueryDesc<T>;
  }

  count(){
    const args = Array.from(arguments); args.unshift(this.collRef);
    return this._fs.count.apply(this._fs, args as Parameters<FstoreService['count']>);
  }

  
}

export class QueryDesc<T> {
  refresh$ = new BehaviorSubject(null);

  data$ = this.refresh$.pipe(
    switchMap(()=>from(getDocs(this.getClauses()))),
    map(qSnap=>qSnap.docs),
    tap(docs=>this.logPageMarker(docs)),
    map(docs=>docs.map(doc=>{ let docData:any = doc.data(); docData['id']=doc.id; return docData as T})),
  ) as Observable<T[]>;

  coll:CollectionReference;
  clauses:QClause<T>[] = [];
  orderClause?:[string,QOrderDirection];
  resultLimit?:number;
  nextPageMarker:QueryDocumentSnapshot<T, DocumentData>[] = [];

  currPage = 0;

  constructor(collection:CollectionReference){
    this.coll =  collection;
  }

  where(clause:{field:CollectionFields<T>, operator:keyof typeof QueryOps, value:any}){
    // console.log(arguments);
    this.clauses.push(clause);
    return this;
  }

  orderBy(field:CollectionFields<T>, direction:QOrderDirection = 'asc'){
    this.orderClause = [field as string,direction];
    return this;
  }

  limit(value:typeof this.resultLimit){
    if (!value || value < 0) return this;
    this.resultLimit = value;
    return this;
  }

  getClauses(){
    let myargs:[query: Query<unknown, DocumentData>, ...queryConstraints: QueryConstraint[]] = [this.coll];
    for (let clause of this.clauses){
      switch (clause.operator){
        // case QueryOps.equal:
        //   myargs.push(where(clause.field as string,QueryOps[clause.operator],clause.value));
        //   break;
        default:
          myargs.push(where(clause.field as string,QueryOps[clause.operator],clause.value));
      }
    }
    if (this.orderClause){
      myargs.push(orderBy(this.orderClause[0], this.orderClause[1]))
    }
    if (this.resultLimit){
      myargs.push(limit(this.resultLimit))
    }
    if (this.currPage > 0){
      // console.log('trying to set next page', this.currPage, this.nextPageMarker[this.currPage - 1]);
      myargs.push(startAfter(this.nextPageMarker[this.currPage - 1]))
    }
    const q = query.apply(null, myargs);
    // console.log(myargs);
    return q as Query<T,DocumentData>;
  }

  logPageMarker(docList:QueryDocumentSnapshot<T, DocumentData>[]){
    if (!docList[docList.length-1]) return;
    const found = this.nextPageMarker.findIndex(item=>item.id == docList[docList.length-1].id );
    if (found == -1){
      this.nextPageMarker.push(docList[docList.length-1]);
    }
  }

  exec(){
    this.refresh$.next(null);
    return this.data$// as Observable<QuerySnapshot<T, DocumentData>>;
    
  }

  count(){
    return from(getCountFromServer<T,DocumentData>(this.getClauses())).pipe(
      map(qSnap=>qSnap.data().count),
    ) as Observable<number>;// as Observable<QuerySnapshot<T, DocumentData>>;

  }

  next(){
    this.currPage++;
    this.refresh$.next(null);
    return this.data$;
  }

  prev(){
    if (this.currPage == 0) return;
    this.currPage--;
    this.refresh$.next(null);
    return this.data$;
  }

}

export type CollectionFields<Table> = keyof Table;
export type QClause<Table> = {
  field:CollectionFields<Table>;
  operator:keyof typeof QueryOps; 
  value:any;
}
export type QOrderDirection = 'desc'|'asc'|undefined;

export const QueryOps = {
  equal:'==' as WhereFilterOp,
  lt:'<' as WhereFilterOp,
  lte:'<=' as WhereFilterOp,
  gt:'>' as WhereFilterOp,
  gte:'>=' as WhereFilterOp,
  neq:'!=' as WhereFilterOp,
  in:'in' as WhereFilterOp,
  notIn:'not-in' as WhereFilterOp,
  contains:'array-contains' as WhereFilterOp,
  containsAny:'array-contains-any' as WhereFilterOp,
}
export type Updateable<Table> = {
  [K in keyof Table]:Table[K]|FieldValue;
}